import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

// ── Supabase service-role client (dashboard read only) ────────────────────────
// This client is separate from the anon client used by the form.
// The service key is baked in at build time — acceptable for an internal
// management view, but do NOT use this client in the outlet form.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const serviceKey  = process.env.REACT_APP_SUPABASE_SERVICE_KEY || '';

const safeUrl = supabaseUrl && supabaseUrl.startsWith('http') ? supabaseUrl : 'https://placeholder.supabase.co';
const safeKey = (serviceKey || process.env.REACT_APP_SUPABASE_ANON_KEY) || 'placeholder-key';

const dashboardSupabase = createClient(safeUrl, safeKey);

// ── Constants ─────────────────────────────────────────────────────────────────

const OUTLETS = ['Velachery', 'T. Nagar', 'Anna Nagar'];
const MAX_DASH_ATTEMPTS = 3;
const DASH_PIN = process.env.REACT_APP_DASHBOARD_PIN || '';

const DATE_RANGES = [
  { label: 'Today',       value: 'today' },
  { label: 'Last 7 days', value: '7days' },
  { label: 'Last 30 days',value: '30days' },
];

const SHIFT_FILTERS = [
  { label: 'All shifts',  value: 'all' },
  { label: '🟢 Good',     value: 'good' },
  { label: '🟡 Okay',     value: 'okay' },
  { label: '🔴 Tough',    value: 'tough' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateRange(rangeKey) {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  if (rangeKey === 'today') return { from: todayStr, to: todayStr };
  const days = rangeKey === '7days' ? 7 : 30;
  const from = new Date(now);
  from.setDate(from.getDate() - (days - 1));
  return { from: from.toISOString().split('T')[0], to: todayStr };
}

function shiftClass(shift) {
  if (!shift) return '';
  if (shift.includes('Good')) return 'good';
  if (shift.includes('Okay') || shift.includes('Okay')) return 'okay';
  if (shift.includes('Tough')) return 'tough';
  return '';
}

function shiftLabel(shift) {
  if (!shift) return '—';
  if (shift.includes('Good')) return '🟢 Good shift';
  if (shift.includes('Okay')) return '🟡 Okay shift';
  if (shift.includes('Tough')) return '🔴 Tough shift';
  return shift;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch (_) {
    return dateStr;
  }
}

// ── PIN Screen (dashboard variant) ───────────────────────────────────────────

function DashPinScreen({ onSuccess }) {
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const locked = attempts >= MAX_DASH_ATTEMPTS;

  function handleSubmit(e) {
    e.preventDefault();
    if (locked) return;
    if (pin === DASH_PIN) {
      onSuccess();
    } else {
      const next = attempts + 1;
      setAttempts(next);
      setPin('');
      if (next >= MAX_DASH_ATTEMPTS) {
        setError('');
      } else {
        setError(
          `Incorrect PIN. ${MAX_DASH_ATTEMPTS - next} attempt${
            MAX_DASH_ATTEMPTS - next === 1 ? '' : 's'
          } remaining.`
        );
      }
    }
  }

  return (
    <div className="pin-screen">
      <div className="pin-logo">Taiwan Maami</div>
      <p className="pin-subtitle">Management dashboard</p>
      <div className="pin-card">
        <h2>Enter dashboard PIN</h2>
        <p className="pin-outlet">Managers only</p>
        {locked ? (
          <p className="pin-locked">
            Too many attempts — please contact your administrator.
          </p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <input
              className={`pin-input${error ? ' has-error' : ''}`}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pin}
              onChange={(e) => {
                setError('');
                setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
              placeholder="••••••"
              autoFocus
              autoComplete="off"
            />
            {error && <p className="pin-error">{error}</p>}
            <button
              className="btn-pin"
              type="submit"
              disabled={pin.length < 1}
            >
              View dashboard
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Photo modal ───────────────────────────────────────────────────────────────

function PhotoModal({ url, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="photo-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Full size photo"
    >
      <button
        className="photo-modal-close"
        onClick={onClose}
        aria-label="Close photo"
      >
        ✕
      </button>
      <img
        src={url}
        alt="Full size"
        className="photo-modal-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── Report Card ───────────────────────────────────────────────────────────────

function ReportCard({ report }) {
  const [photoOpen, setPhotoOpen] = useState(false);

  const staffIssues = Array.isArray(report.staff_issues)
    ? report.staff_issues
    : report.staff_issues
    ? [report.staff_issues]
    : [];

  return (
    <div className="report-card">
      <div className="report-card-header">
        <div>
          <div className="rc-date">
            {formatDate(report.report_date)}
            {report.bills_today != null && (
              <span className="rc-bills-badge">{report.bills_today} bills</span>
            )}
          </div>
          <div className="rc-by">
            Submitted by {report.submitted_by}
            {report.peak_hour && (
              <span className="rc-peak-tag">Peak: {report.peak_hour}</span>
            )}
          </div>
        </div>
        <span className={`shift-badge ${shiftClass(report.shift_overall)}`}>
          {shiftLabel(report.shift_overall)}
        </span>
      </div>

      <div className="report-card-body">
        {/* Customer count */}
        {report.customer_count && (
          <div className="rc-row">
            <span className="rc-row-label">Customers</span>
            <span className="rc-row-value">{report.customer_count}</span>
          </div>
        )}

        {/* Positive feedback */}
        {report.customer_feedback_pos && report.customer_feedback_pos.length > 0 && (
          <div className="rc-row">
            <span className="rc-row-label">Positive feedback</span>
            <div className="rc-chips">
              {report.customer_feedback_pos.map((c) => (
                <span key={c} className="rc-chip pos">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Negative feedback */}
        {report.customer_feedback_neg && report.customer_feedback_neg.length > 0 && (
          <div className="rc-row">
            <span className="rc-row-label">Negative feedback</span>
            <div className="rc-chips">
              {report.customer_feedback_neg.map((c) => (
                <span key={c} className="rc-chip neg">{c}</span>
              ))}
            </div>
            {report.negative_feedback_detail && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.negative_feedback_detail}
              </span>
            )}
          </div>
        )}

        {/* Feedback notes */}
        {report.feedback_notes && (
          <div className="rc-row">
            <span className="rc-row-label">Customer notes</span>
            <span className="rc-row-value">{report.feedback_notes}</span>
          </div>
        )}

        {/* Equipment */}
        {report.equipment_issues && report.equipment_issues.length > 0 && (
          <div className="rc-row">
            <span className="rc-row-label">Equipment issues</span>
            <div className="rc-chips">
              {report.equipment_issues.map((c) => (
                <span key={c} className="rc-chip">{c}</span>
              ))}
            </div>
            {report.equipment_notes && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.equipment_notes}
              </span>
            )}
            {report.equipment_detail && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.equipment_detail}
              </span>
            )}
          </div>
        )}

        {/* Ingredients */}
        {(report.ingredient_issues && report.ingredient_issues.length > 0) || isStockOut(report.stock_issues_text) ? (
          <div className="rc-row">
            <span className="rc-row-label">Stock issues</span>
            {report.ingredient_issues && report.ingredient_issues.length > 0 && (
              <div className="rc-chips">
                {report.ingredient_issues.map((c) => (
                  <span key={c} className="rc-chip">{c}</span>
                ))}
              </div>
            )}
            {report.stock_issues_text && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.stock_issues_text}
              </span>
            )}
            {report.ingredient_notes && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.ingredient_notes}
              </span>
            )}
          </div>
        ) : null}

        {/* Hygiene */}
        {report.hygiene_rating && (
          <div className="rc-row">
            <span className="rc-row-label">Hygiene</span>
            <span className="rc-row-value">{report.hygiene_rating}</span>
            {report.hygiene_issues && report.hygiene_issues.length > 0 && (
              <div className="rc-chips" style={{ marginTop: '4px' }}>
                {report.hygiene_issues.map((c) => (
                  <span key={c} className="rc-chip">{c}</span>
                ))}
              </div>
            )}
            {report.hygiene_notes && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.hygiene_notes}
              </span>
            )}
          </div>
        )}

        {/* Staff */}
        {staffIssues.length > 0 && (
          <div className="rc-row">
            <span className="rc-row-label">Staff</span>
            <div className="rc-chips">
              {staffIssues.map((c) => (
                <span key={c} className="rc-chip">{c}</span>
              ))}
            </div>
            {report.staff_notes && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.staff_notes}
              </span>
            )}
          </div>
        )}

        {/* Delivery */}
        {report.delivery_platform && (
          <div className="rc-row">
            <span className="rc-row-label">Delivery platforms</span>
            <span className="rc-row-value">{report.delivery_platform}</span>
            {report.platform_notes && (
              <span className="rc-row-value" style={{ marginTop: '4px' }}>
                {report.platform_notes}
              </span>
            )}
          </div>
        )}

        {/* Voids */}
        {report.voids_today && (
          <div className="rc-row">
            <span className="rc-row-label">Voids / refunds</span>
            <span className="rc-row-value">
              {report.voids_detail || 'Yes — no details provided'}
            </span>
          </div>
        )}

        {/* Other notes */}
        {report.other_notes && (
          <div className="rc-row">
            <span className="rc-row-label">Other notes</span>
            <span className="rc-row-value">{report.other_notes}</span>
          </div>
        )}

        {/* One thing better */}
        {report.one_thing_better && (
          <div className="rc-row">
            <span className="rc-row-label">One thing better</span>
            <span className="rc-row-value rc-one-thing">{report.one_thing_better}</span>
          </div>
        )}

        {/* Photo */}
        {report.photo_url && (
          <div className="rc-row">
            <span className="rc-row-label">Photo</span>
            <img
              src={report.photo_url}
              alt="Attached file for this report"
              className="rc-photo-thumb"
              onClick={() => setPhotoOpen(true)}
            />
          </div>
        )}
      </div>

      {photoOpen && (
        <PhotoModal url={report.photo_url} onClose={() => setPhotoOpen(false)} />
      )}
    </div>
  );
}

// ── Stock-out sentinel check ─────────────────────────────────────────────────

function isStockOut(text) {
  if (!text) return false;
  const t = text.trim().toLowerCase();
  if (t === '' || t === 'none' || t === 'nothing' || t === 'n/a') return false;
  return true;
}

// ── Summary strip ─────────────────────────────────────────────────────────────

function SummaryStrip({ reports, activeFilter, onFilterChange }) {
  // ── Bills analytics ──────────────────────────────────────────────────────────
  const billsReports = reports.filter((r) => r.bills_today != null);
  const totalBills = billsReports.reduce((sum, r) => sum + (r.bills_today || 0), 0);
  const dailyAvg = billsReports.length > 0
    ? Math.round(totalBills / billsReports.length)
    : null;

  // ── Issue chip counts ────────────────────────────────────────────────────────
  const equipmentCount = reports.filter((r) => {
    const issues = r.equipment_issues || [];
    return issues.some((i) => i && i !== 'No issues');
  }).length;

  const stockOutCount = reports.filter((r) => isStockOut(r.stock_issues_text)).length;

  const complaintsCount = reports.filter((r) => {
    const neg = r.customer_feedback_neg || [];
    return neg.some((i) => i && i !== 'Nothing to flag');
  }).length;

  // ── Colour coding helper ─────────────────────────────────────────────────────
  function chipColour(count) {
    if (count === 0) return 'sc-chip-green';
    if (count <= 2) return 'sc-chip-amber';
    return 'sc-chip-red';
  }

  // ── Recent "one thing better" (last 3 across all dates) ─────────────────────
  const recentFeedback = reports
    .filter((r) => r.one_thing_better && r.one_thing_better.trim())
    .slice(0, 3);

  return (
    <div className="topline-section">
      {/* Row 1: Bills analytics */}
      <div className="topline-bills-row">
        <div className="topline-bills-card">
          <div className="tbc-value">{totalBills > 0 ? totalBills : '—'}</div>
          <div className="tbc-label">Total bills</div>
        </div>
        <div className="topline-bills-card">
          <div className="tbc-value">{dailyAvg != null ? dailyAvg : '—'}</div>
          <div className="tbc-label">Daily avg</div>
        </div>
      </div>

      {/* Row 2: Issue chips */}
      <div className="topline-chips-row">
        <button
          className={`topline-chip ${chipColour(equipmentCount)}${activeFilter === 'equipment' ? ' active' : ''}`}
          onClick={() => onFilterChange(activeFilter === 'equipment' ? null : 'equipment')}
          aria-pressed={activeFilter === 'equipment'}
        >
          <span className="tc-count">{equipmentCount}</span>
          <span className="tc-label">Equipment issues</span>
        </button>
        <button
          className={`topline-chip ${chipColour(stockOutCount)}${activeFilter === 'stockout' ? ' active' : ''}`}
          onClick={() => onFilterChange(activeFilter === 'stockout' ? null : 'stockout')}
          aria-pressed={activeFilter === 'stockout'}
        >
          <span className="tc-count">{stockOutCount}</span>
          <span className="tc-label">Stock-outs</span>
        </button>
        <button
          className={`topline-chip ${chipColour(complaintsCount)}${activeFilter === 'complaints' ? ' active' : ''}`}
          onClick={() => onFilterChange(activeFilter === 'complaints' ? null : 'complaints')}
          aria-pressed={activeFilter === 'complaints'}
        >
          <span className="tc-count">{complaintsCount}</span>
          <span className="tc-label">Complaints</span>
        </button>
      </div>

      {/* Row 3: Recent team feedback */}
      {recentFeedback.length > 0 && (
        <div className="topline-feedback-block">
          <div className="tfb-heading">Recent team feedback</div>
          {recentFeedback.map((r) => (
            <div key={r.id} className="tfb-item">
              <span className="tfb-quote">“{r.one_thing_better}”</span>
              <span className="tfb-meta">{formatDate(r.report_date)} · {r.submitted_by}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

function Dashboard() {
  const [pinPassed, setPinPassed] = useState(false);
  const [activeOutlet, setActiveOutlet] = useState(OUTLETS[0]);
  const [dateRange, setDateRange] = useState('7days');
  const [shiftFilter, setShiftFilter] = useState('all');
  const [toplineFilter, setToplineFilter] = useState(null); // 'equipment' | 'stockout' | 'complaints' | null
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const fetchReports = useCallback(async () => {
    if (!pinPassed) return;
    setLoading(true);
    setFetchError('');

    // DEV PREVIEW: ?mock=1 loads sample data without Supabase
    if (new URLSearchParams(window.location.search).get('mock') === '1') {
      await new Promise((r) => setTimeout(r, 300));
      setReports([
        { id: '1', report_date: '2026-07-02', outlet: activeOutlet, submitted_by: 'Biak',
          shift_overall: 'Good shift', bills_today: 34, peak_hour: '7–8pm',
          customer_feedback_pos: ['Taste', 'Speed'], customer_feedback_neg: ['Wait time too long'],
          negative_feedback_detail: 'Customer at 7pm said wait was 20 mins. Apologised and offered discount.',
          equipment_issues: ['Blender'], equipment_detail: 'Blender making grinding noise. Still working.',
          stock_issues_text: 'Ran out of tapioca pearls at 8pm, affected ~6 orders',
          voids_today: true, voids_detail: '1 void — wrong order, customer refunded',
          one_thing_better: 'A second blender would have helped a lot during the 7–8pm rush.',
          photo_url: null, hygiene_rating: '✅ All clean — no issues',
          ingredient_issues: [], ingredient_notes: '', staff_issues: ['No issues'], staff_notes: '' },
        { id: '2', report_date: '2026-07-01', outlet: activeOutlet, submitted_by: 'Priya',
          shift_overall: 'Okay shift', bills_today: 28, peak_hour: '6–7pm',
          customer_feedback_pos: ['Taste'], customer_feedback_neg: ['Nothing to flag'],
          equipment_issues: ['No issues'], stock_issues_text: 'none',
          voids_today: false, voids_detail: null,
          one_thing_better: 'Better lighting near the counter would help during evenings.',
          photo_url: null, hygiene_rating: '⚠️ Minor issues — sorted during shift',
          ingredient_issues: [], staff_issues: ['No issues'] },
        { id: '3', report_date: '2026-06-30', outlet: activeOutlet, submitted_by: 'Ravi',
          shift_overall: 'Tough shift', bills_today: 19, peak_hour: '8–9pm',
          customer_feedback_pos: [], customer_feedback_neg: ['Wrong order', 'Rude staff'],
          negative_feedback_detail: 'Two complaints about wrong toppings. One about staff tone.',
          equipment_issues: ['POS', 'Printer'], equipment_detail: 'POS crashed twice during peak, had to restart.',
          stock_issues_text: 'Ran out of cups at 9pm, used takeaway boxes for last 10 orders',
          voids_today: true, voids_detail: '3 voids — POS issues caused duplicate charges',
          one_thing_better: 'POS system needs to be replaced urgently before weekend.',
          photo_url: null, hygiene_rating: null,
          ingredient_issues: [], staff_issues: ['Short-staffed all shift'] },
      ]);
      setLoading(false);
      return;
    }

    try {
      const { from, to } = getDateRange(dateRange);

      let query = dashboardSupabase
        .from('outlet_daily_reports')
        .select('*')
        .eq('outlet', activeOutlet)
        .gte('report_date', from)
        .lte('report_date', to)
        .order('report_date', { ascending: false })
        .order('submitted_at', { ascending: false });

      if (shiftFilter !== 'all') {
        // Use ilike for partial match since the full string includes extra text
        const keyword = shiftFilter === 'good' ? 'Good' : shiftFilter === 'okay' ? 'Okay' : 'Tough';
        query = query.ilike('shift_overall', `%${keyword}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setReports(data || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setFetchError('Could not load reports. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [pinPassed, activeOutlet, dateRange, shiftFilter]);

  // Apply topline chip filter to the visible report list
  const visibleReports = toplineFilter === null ? reports : reports.filter((r) => {
    if (toplineFilter === 'equipment') {
      return (r.equipment_issues || []).some((i) => i && i !== 'No issues');
    }
    if (toplineFilter === 'stockout') {
      return isStockOut(r.stock_issues_text);
    }
    if (toplineFilter === 'complaints') {
      return (r.customer_feedback_neg || []).some((i) => i && i !== 'Nothing to flag');
    }
    return true;
  });

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  if (!pinPassed) {
    return <DashPinScreen onSuccess={() => setPinPassed(true)} />;
  }

  return (
    <div className="dashboard-shell">
      {/* Header */}
      <header className="dashboard-header">
        <h1>Daily Reports</h1>
        <div className="dh-sub">Management view — read only</div>
      </header>

      {/* Outlet tabs */}
      <div className="outlet-tabs" role="tablist" aria-label="Outlet">
        {OUTLETS.map((outlet) => (
          <button
            key={outlet}
            role="tab"
            aria-selected={activeOutlet === outlet}
            className={`outlet-tab${activeOutlet === outlet ? ' active' : ''}`}
            onClick={() => setActiveOutlet(outlet)}
          >
            {outlet}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select
          className="filter-select"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          aria-label="Date range"
        >
          {DATE_RANGES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        <select
          className="filter-select"
          value={shiftFilter}
          onChange={(e) => setShiftFilter(e.target.value)}
          aria-label="Shift rating filter"
        >
          {SHIFT_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="dashboard-loading">Loading reports…</div>
      ) : fetchError ? (
        <div className="dashboard-error">
          {fetchError}
          <br />
          <button
            className="btn-retry"
            style={{ marginTop: '16px', display: 'inline-block' }}
            onClick={fetchReports}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <SummaryStrip
            reports={reports}
            activeFilter={toplineFilter}
            onFilterChange={(f) => setToplineFilter(f)}
          />
          {toplineFilter && (
            <div className="topline-filter-banner">
              Showing {visibleReports.length} report{visibleReports.length !== 1 ? 's' : ''} with{' '}
              {toplineFilter === 'equipment' ? 'equipment issues'
                : toplineFilter === 'stockout' ? 'stock-outs'
                : 'complaints'}
              {' '}—{' '}
              <button
                className="topline-filter-clear"
                onClick={() => setToplineFilter(null)}
              >
                Clear filter
              </button>
            </div>
          )}
          <div className="report-list">
            {visibleReports.length === 0 ? (
              <div className="dashboard-empty">
                {toplineFilter ? 'No reports match this filter.' : 'No reports found for this period.'}
              </div>
            ) : (
              visibleReports.map((r) => <ReportCard key={r.id} report={r} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
